/**
 * OverviewTab Component
 * Program overview with description and health metrics
 */

import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaUserFriends, 
  FaCalendarAlt 
} from 'react-icons/fa';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import styles from './TabStyles.module.css';

const EVENT_STATUS_COLORS = {
  Planned: '#1976d2',
  Active: '#14a281',
  Inactive: '#b0b8c1',
  Cancelled: '#dc3545',
  Postponed: '#ffc107',
  Completed: '#4caf50',
};

const OverviewTab = ({ program, clubId }) => {
  const [programEvents, setProgramEvents] = useState([]);
  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Fetch events
  useEffect(() => {
    if (!program?._id) {
      setProgramEvents([]);
      return;
    }
    fetch(`${backendUrl}/api/events/${program._id}`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setProgramEvents(Array.isArray(data) ? data : []))
      .catch(() => setProgramEvents([]));
  }, [program?._id, backendUrl]);

  // Fetch team
  useEffect(() => {
    if (!program?._id) {
      setTeam(null);
      return;
    }
    setTeamLoading(true);
    fetch(`${backendUrl}/api/teams/${program._id}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setTeam(data))
      .catch(() => setTeam(null))
      .finally(() => setTeamLoading(false));
  }, [program?._id, backendUrl]);

  // Calculate event status counts
  const eventStatusList = ['Planned', 'Active', 'Inactive', 'Cancelled', 'Postponed', 'Completed'];
  const eventStatusCounts = eventStatusList.reduce((acc, status) => {
    acc[status] = 0;
    return acc;
  }, {});
  programEvents.forEach(ev => {
    if (eventStatusCounts.hasOwnProperty(ev.status)) {
      eventStatusCounts[ev.status]++;
    }
  });
  const pieData = eventStatusList.map(status => ({ 
    name: status, 
    value: eventStatusCounts[status] 
  }));
  const totalEvents = programEvents.length;

  // KPIs
  const totalCoaches = team ? 1 + (team.assistantCoaches?.length || 0) : 0;
  const totalPlayers = team?.players?.length || 0;
  const maxPlayers = team?.maxPlayers || 0;
  const fillRate = maxPlayers ? Math.round((totalPlayers / maxPlayers) * 100) : 0;

  // Average duration
  const plannedEvents = programEvents.filter(ev => ev.status === 'Planned');
  const avgPlannedDuration = plannedEvents.length > 0
    ? Math.round(plannedEvents.reduce((sum, ev) => 
        sum + ((new Date(ev.dateTime.end) - new Date(ev.dateTime.start)) / 60000), 0
      ) / plannedEvents.length)
    : 0;

  // Next event
  const now = new Date();
  const upcomingEvents = programEvents.filter(ev => new Date(ev.dateTime.start) > now);
  const nextEventDate = upcomingEvents.length > 0
    ? new Date(Math.min(...upcomingEvents.map(ev => new Date(ev.dateTime.start)))).toLocaleDateString()
    : 'N/A';

  return (
    <div className={styles.tabContainer}>
      {/* Description */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>About This Program</h3>
        <p className={styles.description}>
          {program.programDetails || 'No description available.'}
        </p>
        
        <div className={styles.programDates}>
          <div className={styles.dateItem}>
            <span className={styles.dateLabel}>Start Date</span>
            <span className={styles.dateValue}>
              {program.schedule?.startDate 
                ? new Date(program.schedule.startDate).toLocaleDateString() 
                : 'N/A'}
            </span>
          </div>
          <div className={styles.dateItem}>
            <span className={styles.dateLabel}>End Date</span>
            <span className={styles.dateValue}>
              {program.schedule?.endDate 
                ? new Date(program.schedule.endDate).toLocaleDateString() 
                : 'N/A'}
            </span>
          </div>
        </div>
      </section>

      {/* Program Health */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Program Health</h3>
        
        <div className={styles.healthGrid}>
          {/* Pie Chart */}
          <div className={styles.chartCard}>
            <h4 className={styles.chartTitle}>Event Status</h4>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  innerRadius={40}
                  paddingAngle={2}
                  label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                  labelLine={false}
                >
                  {pieData.map((entry) => (
                    <Cell 
                      key={`cell-${entry.name}`} 
                      fill={EVENT_STATUS_COLORS[entry.name] || '#8884d8'} 
                      stroke="#fff" 
                      strokeWidth={1} 
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value}`, `${name}`]} />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.chartLabel}>Total: {totalEvents}</div>
          </div>

          {/* KPIs */}
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <FaUserFriends className={styles.kpiIcon} />
              <div className={styles.kpiContent}>
                <span className={styles.kpiValue}>
                  {teamLoading ? '...' : totalCoaches}
                </span>
                <span className={styles.kpiLabel}>Coaches</span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <FaUsers className={styles.kpiIcon} />
              <div className={styles.kpiContent}>
                <span className={styles.kpiValue}>
                  {teamLoading ? '...' : totalPlayers}
                </span>
                <span className={styles.kpiLabel}>Players</span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <FaUsers className={styles.kpiIcon} />
              <div className={styles.kpiContent}>
                <span className={styles.kpiValue}>
                  {teamLoading ? '...' : `${fillRate}%`}
                </span>
                <span className={styles.kpiLabel}>Fill Rate</span>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill} 
                    style={{ width: `${fillRate}%` }} 
                  />
                </div>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <FaCalendarAlt className={styles.kpiIcon} />
              <div className={styles.kpiContent}>
                <span className={styles.kpiValue}>{avgPlannedDuration}</span>
                <span className={styles.kpiLabel}>Avg. Duration (min)</span>
              </div>
            </div>

            <div className={styles.kpiCard}>
              <FaCalendarAlt className={styles.kpiIcon} />
              <div className={styles.kpiContent}>
                <span className={styles.kpiValue}>{nextEventDate}</span>
                <span className={styles.kpiLabel}>Next Event</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OverviewTab;










